import {
	fetchMapDeployments,
	type ValidateMapDeploymentsResult,
} from './fetchMapDeployments'
import { getJSONFromLocalStorage } from './localStorage'
import type { BaseMapDeployment, ServerReceivedMapDeployments } from './models'

type GetMapDeploymentsOptions<T extends BaseMapDeployment> = {
	localStorageKey: string
	remoteUrl: string
	publicFolderUrl: string
	validationFunction?: (
		data: ServerReceivedMapDeployments<T>
	) => ValidateMapDeploymentsResult<T>
}

export async function getMapDeployments<T extends BaseMapDeployment>({
	localStorageKey,
	remoteUrl,
	publicFolderUrl,
	validationFunction,
}: GetMapDeploymentsOptions<T>) {
	const fetchResults = await fetchMapDeployments<T>({
		localstorageKey: localStorageKey,
		mapDeploymentsFetchUrl: remoteUrl,
		validationFn: validationFunction,
	})

	if (fetchResults.success) {
		// console.log(`fetch from ${remoteUrl} was successful`)
		return fetchResults.data
	}
	const localStorageData =
		getJSONFromLocalStorage<ServerReceivedMapDeployments<T>>(
			localStorageKey
		)
	if (localStorageData.success) {
		// console.log(`fetch from local storage was successful`)
		return localStorageData.data
	}

	// fetch from public folder as a last resort, this is the fallback data that we have in the repo,
	// it should be updated manually if the data changes

	const fetchFromPublicFolder = await fetchMapDeployments<T>({
		localstorageKey: localStorageKey,
		mapDeploymentsFetchUrl: publicFolderUrl,
		skipValidate: true, // we can skip validation here because it's a static file that we control
	})
	if (fetchFromPublicFolder.success) {
		// console.log(`fetch from public folder was successful`)
		return fetchFromPublicFolder.data
	}
	// if all else fails, return an empty array with a creation timestamp and version fallback
	// biome-ignore lint/suspicious/noConsole: we want to log this because it means something went wrong with fetching the data from the server and local storage, so we are falling back to the public folder data which is the last resort
	console.warn(
		`no data was found at ${remoteUrl}, localstorage or ${publicFolderUrl} returning an empty array`
	)
	return {
		deployments: [],
		creationTimestamp: new Date().toISOString(),
		version: 'fallback',
	}
}
